import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Heart, Newspaper, Calendar } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import Loading from "../components/Loading";
import { formatDateDisplay } from "../lib/utils";

const TAG_STYLES = {
  news:       "bg-sky-500/20 text-sky-300 border-sky-500/40",
  release:    "bg-violet-500/20 text-violet-300 border-violet-500/40",
  sale:       "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  discussion: "bg-gold/20 text-gold border-gold/40",
};
const TAG_LABELS = { news: "News", release: "Release", sale: "Sale / Deal", discussion: "Discussion" };

// Normalise raw AI output so headings/bullets always start on their own line
function normaliseMarkdown(md) {
  return md
    // ensure any heading (# ## ###) is preceded by a blank line
    .replace(/([^\n])\n?(#{1,3} )/g, "$1\n\n$2")
    // ensure a heading line is followed by a newline (not more text on same line after the heading text)
    // split "## Heading text **bold** more text..." — headings end at first sentence break or \n
    // ensure bullet items (- at line start after optional whitespace) are preceded by a newline
    .replace(/([^\n])(\n[ \t]*- )/g, "$1\n$2")
    // lone # or ## with no space (stray hash) — remove
    .replace(/^#+\s*$/gm, "")
    // collapse 3+ blank lines to 2
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Strip **bold** markers from plain text (for headings where we don't want bold inside h2)
function stripBold(text) {
  return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1");
}

// Lightweight markdown renderer — handles # ## ### headings, **bold**, bullet lists, paragraphs
function renderMarkdown(raw) {
  const md = normaliseMarkdown(raw);
  const blocks = [];
  const lines = md.split("\n");
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul key={key++} className="list-none space-y-2 my-5">
        {listItems.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-slate-300 leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (/^### (.+)/.test(line)) {
      flushList();
      blocks.push(
        <h3 key={key++} className="text-lg font-bold text-gold/90 mt-7 mb-3">{stripBold(line.replace(/^### /, ""))}</h3>
      );
    } else if (/^## (.+)/.test(line)) {
      flushList();
      blocks.push(
        <h2 key={key++} className="text-xl sm:text-2xl font-black tracking-tight text-white mt-10 mb-4 pb-2 border-b border-gold/20">
          {stripBold(line.replace(/^## /, ""))}
        </h2>
      );
    } else if (/^# (.+)/.test(line)) {
      flushList();
      blocks.push(
        <h2 key={key++} className="text-xl sm:text-2xl font-black tracking-tight text-white mt-10 mb-4 pb-2 border-b border-gold/20">
          {stripBold(line.replace(/^# /, ""))}
        </h2>
      );
    } else if (/^- (.+)/.test(line)) {
      listItems.push(line.replace(/^- /, ""));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      // Standalone CTA link — entire line is a single [text](url)
      const ctaMatch = line.match(/^\[(.+?)\]\((https?:\/\/[^\s)]+)\)$/);
      if (ctaMatch) {
        const [, label, url] = ctaMatch;
        const isPreorder = /pre.?order/i.test(label);
        const isSale = /deal|sale|shop|view/i.test(label);
        
        blocks.push(
          <a
            key={key++}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-xl border font-bold transition-all hover:scale-[1.01] no-underline ${
              isPreorder
                ? "bg-violet-500/10 border-violet-400/30 hover:bg-violet-500/20 text-violet-200"
                : isSale
                ? "bg-emerald-500/10 border-emerald-400/30 hover:bg-emerald-500/20 text-emerald-200"
                : "bg-gold/5 border-gold/30 hover:bg-gold/15 text-gold"
            }`}
          >
            {/* Reduced text size to text-sm and tracking for a cleaner single-line look */}
            <span className="text-sm tracking-tight truncate">{label}</span>
            <span className="text-xs uppercase tracking-widest opacity-50 shrink-0">→</span>
          </a>
        );
      } else {
        blocks.push(
          <p key={key++} className="text-slate-300 leading-relaxed text-base mb-4"
            dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
          />
        );
      }
    }
  }
  flushList();
  return blocks;
}

function inlineFormat(text) {
  return text
    .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, "<a href=\"$2\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-gold underline underline-offset-2 hover:text-gold-light\">$1</a>")
    .replace(/\*\*(.+?)\*\*/g, "<strong class=\"text-white font-bold\">$1</strong>")
    .replace(/\*(.+?)\*/g, "<em class=\"text-slate-200 italic\">$1</em>");
}

function PostCard({ post, liked, onLike, onClick }) {
  return (
    <div
      onClick={onClick}
      className="group relative bg-navy-700 rounded-2xl neon-border overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      {/* Cover image */}
      <div className="relative aspect-[16/9] bg-navy-800 overflow-hidden">
        {post.image ? (
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Newspaper className="w-12 h-12 text-slate-700" />
          </div>
        )}
        {/* Tag pill over image */}
        <span className={`absolute top-3 left-3 text-[9px] uppercase tracking-[0.2em] border px-2.5 py-1 rounded-full font-bold backdrop-blur-sm ${TAG_STYLES[post.tag] || TAG_STYLES.news}`}>
          {TAG_LABELS[post.tag] || post.tag}
        </span>
        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-navy-700 to-transparent" />
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1 gap-2">
        <h2 className="font-black text-base leading-snug group-hover:text-gold transition line-clamp-2">
          {post.title}
        </h2>
        <p className="text-sm text-slate-400 line-clamp-3 leading-relaxed flex-1">{renderMarkdown(post.body)}</p>
        <div className="flex items-center justify-between pt-3 border-t border-gold/10 mt-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Calendar className="w-3 h-3" />
            <span>{formatDateDisplay(post.created_at)}</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onLike(post.id); }}
            disabled={liked}
            className={`flex items-center gap-1.5 text-xs font-bold transition ${liked ? "text-rose-400" : "text-slate-500 hover:text-rose-400"}`}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-rose-400" : ""}`} />
            {post.likes}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function CommunityPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedIds, setLikedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gt_liked_posts") || "[]"); } catch { return []; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/posts");
      setPosts(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLike = async (id) => {
    if (likedIds.includes(id)) return;
    try {
      const r = await api.post(`/posts/${id}/like`);
      setPosts((prev) => prev.map((p) => (p.id === id ? r.data : p)));
      const next = [...likedIds, id];
      setLikedIds(next);
      localStorage.setItem("gt_liked_posts", JSON.stringify(next));
    } catch {}
  };

  return (
    <div>
      <Helmet>
        <title>{"Community — News, Releases & Deals | GamesTrade"}</title>
        <meta name="description" content="Latest gaming news, new releases, deals and discussions from the GamesTrade team. Stay updated on PS5, Nintendo Switch, Xbox and more." />
        <link rel="canonical" href="https://gamestrade.in/community" />
        <meta property="og:title" content="Community — GamesTrade" />
        <meta property="og:description" content="Latest gaming news, releases and deals from GamesTrade, Noida." />
        <meta property="og:url" content="https://gamestrade.in/community" />
        <meta property="og:type" content="website" />
      </Helmet>
      <PageHero
        eyebrow="GamesTrade Community"
        title={<>Latest <span className="text-gold">posts</span></>}
        description="News, releases, deals and discussions from the GamesTrade team."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Community" }]}
        size="compact"
      />
      <div className="site-container py-12">

      {loading ? (
        <Loading variant="block" label="Loading posts" />
      ) : posts.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <Newspaper className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <div className="text-sm uppercase tracking-wider">No posts yet.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              liked={likedIds.includes(post.id)}
              onLike={handleLike}
              onClick={() => navigate(`/community/${post.id}`)}
            />
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
